
import { defineComponent, onBeforeMount, PropType, reactive } from 'vue'

// Components
import Modal from '@/common/components/UI/Modal/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'

// Classes
import { DataState } from '@/core/classes/DataState.class'

// Realisations
import { billing } from '@/core/realisations/billing.realisation'
import { companies } from '@/core/realisations/companies.realisation'

// Types
import { CompanyT } from '@/core/types/companies.types'

// Validations
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'

// Composable
import useNotifications from '@/common/composable/useNotifications'

export default defineComponent({
    components: {
        Modal,
        TheSelect,
    },
    emits: ['cancel', 'generated'],
    setup(props, { emit }) {
        const notifications = useNotifications()

        const state = reactive({
            is_loading: false,
            company_id: null as number | null,
        })

        const state_form$ = useVuelidate({ company_id: { required } }, state)

        const data_state = reactive({
            companies: new DataState<CompanyT>({}),
        })

        const emitCancel = () => {
            if (state.is_loading) return
            emit('cancel')
        }

        const loadCompanies = async (page: number, search?: string) => {
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true
                const response = await companies.getList({
                    page,
                    search,
                    limit: 9999,
                })
                data_state.companies.list = response.data

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const loadMoreCompanies = async (page: number) => {
            if (page > data_state.companies.pagination.total_pages) return
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true

                const response = await companies.getList({ page })
                data_state.companies.list = data_state.companies.list.concat(response.data)

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        onBeforeMount(() => {
            loadCompanies(1)
        })

        const generateTranscript = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid || state.is_loading) return

            try {
                state.is_loading = true

                const company_id = state.company_id as number
                await billing.generateCompanyTranscript(company_id)

                notifications.notify({ type: 'success', title: 'Транскрипт сгенерирован' })

                emit('generated')
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При генерации транскрипта произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        return {
            state,
            state_form$,
            data_state,
            emitCancel,
            loadCompanies,
            loadMoreCompanies,
            generateTranscript,
        }
    },
})
